import axios from "axios";

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "https://localhost:7231";

axios.defaults.withCredentials = true;

export async function renderDevPreview(payload) {
    const response = await axios.post(
        `${API_BASE_URL}/api/dev-render/preview`,
        payload,
        { withCredentials: true }
    );

    return response.data;
}

export async function generatePublicPreviewQa(payload) {
    const response = await axios.post(
        `${API_BASE_URL}/api/website/preview-generate`,
        payload,
        { withCredentials: true }
    );

    return response.data;
}

export async function getDevDiagnosticProjects(take = 80) {
    const response = await axios.get(
        `${API_BASE_URL}/api/dev-diagnostics/projects`,
        {
            params: { take },
            withCredentials: true
        }
    );

    return response.data;
}

export async function getPublicPreviewQa(previewSessionId, variantKey = null) {
    const query = variantKey ? `?variantKey=${encodeURIComponent(variantKey)}` : "";

    const response = await axios.get(
        `${API_BASE_URL}/api/website/preview-session/${previewSessionId}${query}`,
        { withCredentials: true }
    );

    return response.data;
}

export async function getPublicPreviewQaStatus(previewSessionId) {
    const response = await axios.get(
        `${API_BASE_URL}/api/website/preview-session/${previewSessionId}/status`,
        { withCredentials: true }
    );

    return response.data;
}

export async function getPublicPreviewQaDocument(previewSessionId, pageKey = "home", variantKey = null) {
    const params = {
        page: pageKey || "home"
    };

    if (variantKey) {
        params.v = variantKey;
    }

    const response = await axios.get(
        `${API_BASE_URL}/api/website/preview-session/${previewSessionId}/document`,
        {
            params,
            withCredentials: true,
            responseType: "text"
        }
    );

    return response.data;
}

export async function getPublicPreviewQaAsset(previewSessionId, assetName) {
    const response = await axios.get(
        `${API_BASE_URL}/api/website/preview-session/${previewSessionId}/assets/${assetName}`,
        {
            withCredentials: true,
            responseType: "text"
        }
    );

    return response.data;
}

export async function getPublicPreviewQaDiagnostics(previewSessionId) {
    const response = await axios.get(
        `${API_BASE_URL}/api/website/preview-session/${previewSessionId}/qa`,
        { withCredentials: true }
    );

    return response.data;
}

export async function waitForPublicPreviewQa(previewSessionId, options = {}) {
    const {
        maxAttempts = 45,
        delayMs = 2000,
        onTick
    } = options;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        const statusResult = await getPublicPreviewQaStatus(previewSessionId);
        const status = String(statusResult?.status || statusResult?.Status || "").toLowerCase();

        if (onTick) {
            onTick({
                attempt,
                maxAttempts,
                status,
                statusResult
            });
        }

        if (
            status === "generated" ||
            status === "completed" ||
            status === "complete" ||
            status === "ready" ||
            status === "succeeded" ||
            status === "success"
        ) {
            return statusResult;
        }

        if (
            status === "failed" ||
            status === "error" ||
            status === "cancelled" ||
            status === "canceled"
        ) {
            throw new Error(
                statusResult?.message ||
                statusResult?.error ||
                statusResult?.errorMessage ||
                "Preview generation failed."
            );
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    throw new Error("Preview generation timed out. Please check backend worker/status.");
}

export async function getProjectCrmRenderQa(projectId, moduleKey) {
    const response = await axios.get(
        `${API_BASE_URL}/api/dev-diagnostics/project/${projectId}/crm-render-qa`,
        {
            params: { moduleKey },
            withCredentials: true
        }
    );

    return response.data;
}

export async function generateV2PreviewQa(payload) {
    const response = await axios.post(
        `${API_BASE_URL}/api/website-v2/preview-generate`,
        payload,
        {
            headers: {
                "X-Nexus-QA": "true"
            },
            withCredentials: true
        }
    );
    return response.data;
}

export async function getV2PreviewQa(previewSessionId) {
    const response = await axios.get(
        `${API_BASE_URL}/api/website-v2/preview-session/${previewSessionId}`,
        { withCredentials: true }
    );

    return response.data;
}

export async function getV2PreviewQaDocument(previewSessionId, pageKey = "home") {
    const response = await axios.get(
        `${API_BASE_URL}/api/website-v2/preview-session/${previewSessionId}/document`,
        {
            params: {
                page: pageKey || "home"
            },
            withCredentials: true,
            responseType: "text"
        }
    );

    return response.data;
}

export async function getV2PreviewQaAsset(previewSessionId, assetName) {
    const response = await axios.get(
        `${API_BASE_URL}/api/website-v2/preview-session/${previewSessionId}/assets/${assetName}`,
        {
            withCredentials: true,
            responseType: "text"
        }
    );

    return response.data;
}

export async function compareV2PreviewQa(payload) {
    const response = await axios.post(
        `${API_BASE_URL}/api/website-v2/compare-preview`,
        payload,
        { withCredentials: true }
    );

    return response.data;
}

export async function claimV2PreviewQa(previewSessionId, accountId) {
    const response = await axios.post(
        `${API_BASE_URL}/api/website-v2/preview-session/${previewSessionId}/claim`,
        { accountId },
        { withCredentials: true }
    );

    return response.data;
}

export async function getV2ProjectDebug(projectId) {
    const response = await axios.get(
        `${API_BASE_URL}/api/website-v2/project/${projectId}/debug`,
        { withCredentials: true }
    );

    return response.data;
}

export async function generateV2VisualPreview(payload) {
    const response = await axios.post(
        `${API_BASE_URL}/api/dev-render/v2-visual-preview`,
        payload,
        { withCredentials: true }
    );

    return response.data;
}